import { useState } from 'react';

import { deleteData, post, put } from '@preqin/preqin-core/cjs';

import { ReportFilter } from '@/api/types';

import { AlertPriority, ToastAlertProps } from '../components/alert/types';

interface Props {
  reportFilters: ReportFilter[];
  setModalOpen: (value) => void;
  setSelectedFilter: (value) => void;
  setDeleteModalOpen: (value) => void;
  setAlertMessage: (value: ToastAlertProps) => void;
}

export const useFilter = ({
  reportFilters,
  setModalOpen,
  setSelectedFilter,
  setDeleteModalOpen,
  setAlertMessage,
}: Props) => {
  const [filters, setFilters] = useState<ReportFilter[] | []>(reportFilters);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const saveFilter = async (filter, reportId) => {
    setIsLoading(true);
    await post(`${window._env_.POWER_BI_LIST_URL}/reports/${reportId}/filters`, filter)
      .then((res) => {
        if (res.status === 200) {
          setFilters([...filters, res.data]);
          setIsLoading(false);
          setModalOpen(false);
          setSelectedFilter(filter.name);
          setAlertMessage({
            showNotification: true,
            message: `You have successfully saved your search: ${filter.name}`,
            priority: AlertPriority.success,
          });
        } else {
          setIsLoading(false);
          setError(res.data);
          setSelectedFilter(null);
        }
      })
      .catch((err) => {
        setError(err);
        setIsLoading(false);
        setSelectedFilter(null);
      });
  };

  const updateFilter = (filter, reportId, filterId) => {
    setIsLoading(true);
    (async function () {
      await put(`${window._env_.POWER_BI_LIST_URL}/reports/${reportId}/filters/${filterId}`, {
        settings: filter.settings,
      })
        .then((res) => {
          if (res.status === 200) {
            const otherFilters = filters.filter((item: ReportFilter) => item.id !== res.data.id);
            setFilters([...otherFilters, res.data]);
            setIsLoading(false);
            setModalOpen(false);
            setAlertMessage({
              showNotification: true,
              message: 'Save search updated successfully',
              priority: AlertPriority.success,
            });
          } else {
            setIsLoading(false);
            setError(res.data);
          }
        })
        .catch((err) => {
          setError(err);
          setIsLoading(false);
        });
    })();
  };

  // filterList => List of filters which contains id, user_id and report_id
  // response contains only deleted filter ids
  const deleteFilters = (idsToDelete, reportId) => {
    setIsLoading(true);
    (async function () {
      await deleteData(`${window._env_.POWER_BI_LIST_URL}/reports/${reportId}/filters?${idsToDelete}`)
        .then((res) => {
          if (res.status === 200) {
            const otherFilters = filters.filter((item: ReportFilter) => !res.data.includes(item.id));
            setFilters(otherFilters);
            setIsLoading(false);
            setModalOpen(false);
            setDeleteModalOpen(false);
            setAlertMessage({
              showNotification: true,
              message: 'Search has been deleted successfully',
              priority: AlertPriority.success,
            });
          } else {
            setIsLoading(false);
            setError(res.data);
          }
        })
        .catch((err) => {
          setError(err);
          setIsLoading(false);
        });
    })();
  };

  return { filters, isLoading, error, saveFilter, updateFilter, deleteFilters, setFilters };
};
